import CollapsedLogoIcon from '../CollapsedLogoIcon';
import { DASHBOARD_PATH } from '@/common/main/config';
import Link from '@mui/material/Link';
import LogoIcon from '@/common/presentation/components/LogoIcon';
import { Link as RouterLink } from 'react-router-dom';
import { memo, useState, useEffect } from 'react';
import { Box } from '@mui/material';

interface LogoProps {
  expanded: boolean;
  clientLogo?: string | null;
  loading?: boolean;
  fallbackText?: string;
}

const Logo = ({ expanded, clientLogo, fallbackText }: LogoProps) => {
  const [imageError, setImageError] = useState(false);

  // Reset error state when client logo changes
  useEffect(() => {
    setImageError(false);
  }, [clientLogo]);

  // If we have a client logo and it hasn't errored, display it
  if (clientLogo && !imageError) {
    return (
      <Link component={RouterLink} to={DASHBOARD_PATH} aria-label="client-logo">
        <Box
          component="img"
          src={`data:image/jpeg;base64,${clientLogo}`}
          alt="Client Logo"
          sx={{
            height: expanded ? '40px' : '32px',
            maxWidth: expanded ? '150px' : '62px',
            objectFit: 'contain',
            display: 'block',
          }}
          onError={() => {
            // If image fails to load, set error state to show fallback
            console.error('Failed to load client logo image, showing default logo');
            setImageError(true);
          }}
        />
      </Link>
    );
  }

  // Default logo fallback (shown when no client logo or if it fails to load)
  if (fallbackText) {
    return (
      <Link component={RouterLink} to={DASHBOARD_PATH} aria-label="fallback-logo" sx={{ display: 'flex' }}>
        <Box
          sx={{
            height: expanded ? '40px' : '32px',
            minWidth: expanded ? '40px' : '32px',
            px: expanded ? 1 : 0.5,
            borderRadius: '4px',
            bgcolor: '#4B5563',
            color: '#fff',
            fontWeight: 700,
            fontSize: expanded ? '0.95rem' : '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {fallbackText}
        </Box>
      </Link>
    );
  }

  return (
    <Link component={RouterLink} to={DASHBOARD_PATH} aria-label="theme-logo" sx={{ display: 'flex' }}>
      {expanded ? <LogoIcon /> : <CollapsedLogoIcon />}
    </Link>
  );
};

export default memo(Logo);
